/**
 * Circuit Analytics & Progression System
 * Provides pace analysis, target times, and auto-progression for circuit training
 */

import { getSessionHistory, type SessionLog, type CircuitSetLog } from './sessionStorage';
import { normalizeExerciseMatchKey } from './exerciseDatabase';

export interface CircuitHistory {
  exerciseName: string;
  date: string;
  rounds: CircuitSetLog[];
  avgTime: number; // in seconds
  bestTime: number; // in seconds
  totalRPE: number;
}

export interface CircuitPaceAnalysis {
  round: number;
  timeSeconds: number;
  paceChange: 'faster' | 'slower' | 'consistent' | 'first';
  paceChangePercent: number;
  emoji: string;
}

export interface CircuitTargets {
  targetTimePerRound: number; // in seconds
  progressionGoal: number; // suggested improvement in seconds
  lastBestTime: number | null; // best round from last session
  allTimeBestTime: number | null; // best round ever
  lastSessionAvg: number | null; // avg time from last session
}

/**
 * Convert MM:SS string to seconds
 */
export function timeToSeconds(timeStr: string): number {
  if (!timeStr || timeStr === '') return 0;
  
  const parts = timeStr.split(':');
  if (parts.length !== 2) return 0;
  
  const minutes = parseInt(parts[0]) || 0;
  const seconds = parseInt(parts[1]) || 0;
  
  return minutes * 60 + seconds;
}

/**
 * Convert seconds to MM:SS string
 */
export function secondsToTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get circuit history for a specific exercise
 */
 export async function getCircuitHistory(exerciseName: string, limit: number = 10): Promise<CircuitHistory[]> {
  const sessions = await getSessionHistory();
  const history: CircuitHistory[] = [];
  const targetMatchKey = normalizeExerciseMatchKey(exerciseName);
  
  for (const session of sessions) {
    // Only process PT/assessment sessions that have exercises
    if (session.sessionType === 'range' || !session.exercises) continue;
    
    for (const exercise of session.exercises) {
      /**
       * Canonical key, not a bidirectional substring.
       *
       * "Plank".includes("plank") is true for "Side Plank", so querying one
       * pooled the other — two movements with different difficulty and
       * different sustainable durations, averaged together as if they were
       * the same exercise. normalizeExerciseMatchKey collapses genuine
       * spelling variants without merging distinct movements, and is what
       * getExerciseHistory already uses.
       */
      if (normalizeExerciseMatchKey(exercise.exerciseName) === targetMatchKey) {
        if (exercise.circuitSets && exercise.circuitSets.length > 0) {
          const times = exercise.circuitSets
            .filter(s => s.time && s.time !== '')
            .map(s => timeToSeconds(s.time));
          
          if (times.length > 0) {
            const avgTime = times.reduce((a, b) => a + b, 0) / times.length;
            const bestTime = Math.min(...times);
            // Divide by the number of rounds that actually CARRY an RPE, not
            // by every round. Summing the filtered rounds and dividing by the
            // unfiltered length dilutes the average toward zero in proportion
            // to how many rounds went unrated — an audited circuit with one
            // rated round at RPE 9.0 scored 2.57, so a maximal effort read as
            // easy and the engine added volume.
            const ratedRounds = exercise.circuitSets.filter(s => s.rpe !== null);
            const totalRPE = ratedRounds.length > 0
              ? ratedRounds.reduce((sum, s) => sum + (s.rpe || 0), 0) / ratedRounds.length
              : 0;
            
            history.push({
              exerciseName: exercise.exerciseName,
              date: typeof session.date === 'string' ? session.date : session.date.toISOString(),
              rounds: exercise.circuitSets,
              avgTime,
              bestTime,
              totalRPE,
            });
          }
        }
      }
    }
    
    if (history.length >= limit) break;
  }
  
  return history;
}

/**
 * Analyze pace changes between rounds
 */
export function analyzePace(rounds: { time: string; completed: boolean }[]): CircuitPaceAnalysis[] {
  const analysis: CircuitPaceAnalysis[] = [];
  
  for (let i = 0; i < rounds.length; i++) {
    const currentTime = timeToSeconds(rounds[i].time);
    
    if (i === 0 || currentTime === 0) {
      analysis.push({
        round: i + 1,
        timeSeconds: currentTime,
        paceChange: 'first',
        paceChangePercent: 0,
        emoji: '',
      });
    } else {
      const previousTime = timeToSeconds(rounds[i - 1].time);
      
      if (previousTime === 0) {
        analysis.push({
          round: i + 1,
          timeSeconds: currentTime,
          paceChange: 'first',
          paceChangePercent: 0,
          emoji: '',
        });
      } else {
        const diff = currentTime - previousTime;
        const percentChange = (diff / previousTime) * 100;
        
        let paceChange: 'faster' | 'slower' | 'consistent';
        let emoji: string;
        
        if (Math.abs(percentChange) < 3) {
          // Within 3% is considered consistent
          paceChange = 'consistent';
          emoji = '';
        } else if (diff < 0) {
          // Faster than previous round
          paceChange = 'faster';
          emoji = '';
        } else {
          // Slower than previous round
          paceChange = 'slower';
          emoji = '';
        }
        
        analysis.push({
          round: i + 1,
          timeSeconds: currentTime,
          paceChange,
          paceChangePercent: Math.abs(percentChange),
          emoji,
        });
      }
    }
  }
  
  return analysis;
}

/**
 * Get default circuit targets (for synchronous inline usage)
 */
export function getCircuitTargetsSync(): CircuitTargets {
  return {
    targetTimePerRound: 120, // Default 2:00 per round
    progressionGoal: 0,
    lastBestTime: null,
    allTimeBestTime: null,
    lastSessionAvg: null,
  };
}

/**
 * Calculate target times and progression goals
 */
export async function getCircuitTargets(exerciseName: string): Promise<CircuitTargets> {
  const history = await getCircuitHistory(exerciseName, 5);
  
  if (history.length === 0) {
    return {
      targetTimePerRound: 120, // Default 2:00 per round
      progressionGoal: 0,
      lastBestTime: null,
      allTimeBestTime: null,
      lastSessionAvg: null,
    };
  }
  
  // Get last session data
  const lastSession = history[0];
  const lastBestTime = lastSession.bestTime;
  const lastSessionAvg = lastSession.avgTime;
  
  // Get all-time best
  const allTimeBestTime = Math.min(...history.map(h => h.bestTime));
  
  // Calculate target: aim to beat last session's best by 5-10 seconds
  // OR maintain all-time best if it's significantly better
  let targetTimePerRound: number;
  let progressionGoal: number;
  
  if (allTimeBestTime < lastBestTime - 10) {
    // If all-time best is much better, aim for that
    targetTimePerRound = allTimeBestTime;
    progressionGoal = lastBestTime - allTimeBestTime;
  } else {
    // Otherwise, aim to beat last session by 5-10 seconds
    const improvement = Math.max(5, Math.min(10, lastBestTime * 0.05)); // 5-10 sec or 5% of time
    targetTimePerRound = Math.max(30, lastBestTime - improvement); // Don't go below 30 sec
    progressionGoal = improvement;
  }
  
  return {
    targetTimePerRound: Math.round(targetTimePerRound),
    progressionGoal: Math.round(progressionGoal),
    lastBestTime,
    allTimeBestTime,
    lastSessionAvg,
  };
}

/**
 * Compare current session to last session (sync version - returns defaults)
 */
export function compareToLastSession(
  exerciseName: string,
  currentRounds: { time: string; rpe: number | null }[]
): {
  isImproving: boolean;
  avgTimeDiff: number; // negative = faster
  bestTimeDiff: number; // negative = faster
  message: string;
} {
  // Return optimistic defaults for sync usage (async version would load history)
  return {
    isImproving: true,
    avgTimeDiff: 0,
    bestTimeDiff: 0,
    message: 'Complete rounds to see comparison',
  };
}

/**
 * Compare current session to last session (async version with history lookup)
 */
export async function compareToLastSessionAsync(
  exerciseName: string,
  currentRounds: { time: string; rpe: number | null }[]
): Promise<{
  isImproving: boolean;
  avgTimeDiff: number; // negative = faster
  bestTimeDiff: number; // negative = faster
  message: string;
}> {
  const history = await getCircuitHistory(exerciseName, 1);
  
  if (history.length === 0) {
    return {
      isImproving: true,
      avgTimeDiff: 0,
      bestTimeDiff: 0,
      message: 'First time doing this circuit!',
    };
  }
  
  const lastSession = history[0];
  const currentTimes = currentRounds
    .map(r => timeToSeconds(r.time))
    .filter(t => t > 0);
  
  if (currentTimes.length === 0) {
    return {
      isImproving: true,
      avgTimeDiff: 0,
      bestTimeDiff: 0,
      message: 'Complete rounds to see comparison',
    };
  }
  
  const currentAvg = currentTimes.reduce((a, b) => a + b, 0) / currentTimes.length;
  const currentBest = Math.min(...currentTimes);
  
  const avgTimeDiff = currentAvg - lastSession.avgTime;
  const bestTimeDiff = currentBest - lastSession.bestTime;
  
  let message: string;
  const isImproving = avgTimeDiff < 0;
  
  if (Math.abs(avgTimeDiff) < 3) {
    message = ' Maintaining pace from last time';
  } else if (isImproving) {
    message = ` ${Math.abs(avgTimeDiff).toFixed(0)}s faster per round than last time!`;
  } else {
    message = ` ${avgTimeDiff.toFixed(0)}s slower per round than last time`;
  }
  
  return {
    isImproving,
    avgTimeDiff,
    bestTimeDiff,
    message,
  };
}

/**
 * Generate auto-progression suggestion for next workout
 */
export function getProgressionSuggestion(exerciseName: string): string {
  const targets = getCircuitTargetsSync();
  
  if (targets.lastBestTime === null) {
    return ` Aim for ${secondsToTime(targets.targetTimePerRound)} per round`;
  }
  
  if (targets.progressionGoal > 0) {
    return ` Beat last time: ${secondsToTime(targets.lastBestTime)} → ${secondsToTime(targets.targetTimePerRound)} per round`;
  } else {
    return ` Maintain your best: ${secondsToTime(targets.allTimeBestTime || targets.targetTimePerRound)} per round`;
  }
}

